import React, { useState, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, ChevronUp } from 'lucide-react'
import { userKanriService } from '../services/userKanriService'
import { useBlobUrlFromApi } from '../hooks/useBlobUrl'
import Button from '../../shared/ui/Button'

// Treat 1 or "1" as enabled (API may return string)
const companyHasFeature = (companyInfo, key) => Number(companyInfo?.[key]) === 1

const UserDataTable = React.memo(({ users, onEditUser, companyInfo }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(30)
  const [sortField, setSortField] = useState('')
  const [sortDirection, setSortDirection] = useState('asc')
  const [imageRefreshKey, setImageRefreshKey] = useState(Date.now())
  const prevUsersRef = useRef(users)

  // Calculate pagination
  const totalItems = users.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems)
  const currentUsers = users.slice(startIndex, endIndex)

  // Sorting function (supports string and number fields; numeric flags use 0/1)
  const sortUsers = (users, field, direction) => {
    if (!field) return users

    return [...users].sort((a, b) => {
      let aValue = a[field]
      let bValue = b[field]
      const aEmpty = aValue === undefined || aValue === null || aValue === ''
      const bEmpty = bValue === undefined || bValue === null || bValue === ''
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1

      const isNumeric = typeof aValue === 'number' && typeof bValue === 'number'
      if (isNumeric) {
        const cmp = aValue - bValue
        return direction === 'desc' ? -cmp : cmp
      }

      const aStr = String(aValue)
      const bStr = String(bValue)
      if (direction === 'desc') {
        return bStr.localeCompare(aStr, 'ja')
      }
      return aStr.localeCompare(bStr, 'ja')
    })
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedUsers = sortUsers(currentUsers, sortField, sortDirection)

  // Update refresh key whenever users array changes to force image refresh
  // This ensures fresh images are displayed after any user data update (e.g., profile image upload)
  // Even if profileImageId doesn't change, the image content might have changed
  useEffect(() => {
    // Only update if users array reference actually changed (prevents unnecessary updates)
    if (prevUsersRef.current !== users) {
      // Always refresh images when users array changes - image content may have changed
      // even if profileImageId stayed the same
      setImageRefreshKey(Date.now())
      prevUsersRef.current = users
    }
  }, [users])

  // Profile Image Component with automatic blob URL cleanup
  const ProfileImage = React.memo(({ user, refreshKey }) => {
    // Create stable fetch function with useCallback
    const fetchProfileImage = React.useCallback(async () => {
      if (!user.profileImageId) {
        return null
      }

      try {
        return await userKanriService.getProfileImage(
          user.pixUserId,
          user.profileImageId,
          user.filenameExtension
        )
      } catch (error) {
        console.error('Failed to load profile image:', error)
        return null
      }
    }, [user.pixUserId, user.profileImageId, user.filenameExtension])

    // Use blob URL hook for automatic cleanup
    // Include refreshKey in dependencies to create new cache key when refresh is needed
    // This ensures fresh images are fetched even if profileImageId didn't change
    const { blobUrl: imageUrl, isLoading } = useBlobUrlFromApi(
      user.profileImageId ? fetchProfileImage : null,
      // Dependencies - include refreshKey to force new cache entry when users array updates
      [user.pixUserId, user.profileImageId, user.filenameExtension, refreshKey],
      // MIME type
      'image/jpeg'
    )

    return (
      <div className="w-16 h-16 rounded overflow-hidden bg-gray-200 flex items-center justify-center">
        {isLoading ? (
          <div className="w-full h-full bg-gray-200 animate-pulse" />
        ) : (
          <img
            src={imageUrl || '/core/img/noprofileimage.png'}
            alt="Profile"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = '/core/img/noprofileimage.png'
            }}
          />
        )}
      </div>
    )
  })
  ProfileImage.displayName = 'ProfileImage'

  const getRowBackground = (index, riyoKahi) => {
    if (riyoKahi === '利用不可') {
      return 'bg-gray-400'
    }
    return index % 2 === 0 ? 'bg-white' : 'bg-gray-100'
  }

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronDown className="h-4 w-4 text-gray-400" />
    }
    return sortDirection === 'asc' ?
      <ChevronUp className="h-4 w-4 text-blue-600" /> :
      <ChevronDown className="h-4 w-4 text-blue-600" />
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('profileImage')}
              >
                <div className="flex items-center space-x-1">
                  <span>画像</span>
                  {renderSortIcon('profileImage')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('userName')}
              >
                <div className="flex items-center space-x-1">
                  <span>ユーザー名</span>
                  {renderSortIcon('userName')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('mailAddress')}
              >
                <div className="flex items-center space-x-1">
                  <span>メールアドレス</span>
                  {renderSortIcon('mailAddress')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('pixtagRiyo')}
              >
                <div className="flex items-center space-x-1">
                  <span>pix-label利用状況</span>
                  {renderSortIcon('pixtagRiyo')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('pixtagSerialNo')}
              >
                <div className="flex items-center space-x-1">
                  <span>pix-labelシリアルNo</span>
                  {renderSortIcon('pixtagSerialNo')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('riyoKyoten')}
              >
                <div className="flex items-center space-x-1">
                  <span>利用拠点</span>
                  {renderSortIcon('riyoKyoten')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('riyoKahi')}
              >
                <div className="flex items-center space-x-1">
                  <span>利用可否</span>
                  {renderSortIcon('riyoKahi')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('riyoKengen')}
              >
                <div className="flex items-center space-x-1">
                  <span>利用権限</span>
                  {renderSortIcon('riyoKengen')}
                </div>
              </th>
              {companyHasFeature(companyInfo, 'pixClientUsage') && (
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('pixClientUsage')}
                >
                  <div className="flex items-center space-x-1">
                    <span>PIX Client</span>
                    {renderSortIcon('pixClientUsage')}
                  </div>
                </th>
              )}
              {companyHasFeature(companyInfo, 'pixCameraUsage') && (
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('pixCameraUsage')}
                >
                  <div className="flex items-center space-x-1">
                    <span>PIX カメラ</span>
                    {renderSortIcon('pixCameraUsage')}
                  </div>
                </th>
              )}
              {companyHasFeature(companyInfo, 'voiceRecognitionUsage') && (
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('voiceRecognitionUsage')}
                >
                  <div className="flex items-center space-x-1">
                    <span>音声認識</span>
                    {renderSortIcon('voiceRecognitionUsage')}
                  </div>
                </th>
              )}
              {companyHasFeature(companyInfo, 'ocrUsage') && (
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('ocrUsage')}
                >
                  <div className="flex items-center space-x-1">
                    <span>OCR</span>
                    {renderSortIcon('ocrUsage')}
                  </div>
                </th>
              )}
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('thetaCameraUsage')}
              >
                <div className="flex items-center space-x-1">
                  <span>THETA カメラ</span>
                  {renderSortIcon('thetaCameraUsage')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('riyoKaishibi')}
              >
                <div className="flex items-center space-x-1">
                  <span>利用開始日</span>
                  {renderSortIcon('riyoKaishibi')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('lastLoginTime')}
              >
                <div className="flex items-center space-x-1">
                  <span>最終ログイン時刻</span>
                  {renderSortIcon('lastLoginTime')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedUsers.map((user, index) => (
              <tr
                key={user.pixUserId}
                className={`${getRowBackground(index, user.riyoKahi)} hover:bg-gray-50`}
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex justify-center">
                    <ProfileImage 
                      key={`${user.pixUserId}-${user.profileImageId}-${imageRefreshKey}`}
                      user={user} 
                      refreshKey={imageRefreshKey} 
                    />
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {user.userName}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {user.mailAddress}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {user.pixtagRiyo || ''}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {user.pixtagSerialNo || ''}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {user.riyoKyoten || ''}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {user.riyoKahi}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {user.riyoKengen}
                </td>
                {companyHasFeature(companyInfo, 'pixClientUsage') && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {user.pixClientUsage === 1 ? '利用可' : '利用不可'}
                  </td>
                )}
                {companyHasFeature(companyInfo, 'pixCameraUsage') && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {user.pixCameraUsage === 1 ? '利用可' : '利用不可'}
                  </td>
                )}
                {companyHasFeature(companyInfo, 'voiceRecognitionUsage') && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {user.voiceRecognitionUsage === 1 ? '利用可' : '利用不可'}
                  </td>
                )}
                {companyHasFeature(companyInfo, 'ocrUsage') && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {user.ocrUsage === 1 ? '利用可' : '利用不可'}
                  </td>
                )}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {user.thetaCameraUsage === 1 ? '利用可' : '利用不可'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {user.riyoKaishibi}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {user.lastLoginTime || ''}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                  <Button
                    onClick={() => onEditUser(user)}
                    variant="primary"
                    size="sm"
                    className="text-xs"
                  >
                    更新
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <Button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
            >
              前へ
            </Button>
            <Button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              variant="outline"
              size="sm"
            >
              次へ
            </Button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                <span className="font-medium">{startIndex + 1}</span> から{' '}
                <span className="font-medium">{endIndex}</span> まで{' '}
                <span className="font-medium">{totalItems}</span> 件中
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm space-x-1" aria-label="Pagination">
                <Button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                  className="rounded-l-md"
                  icon={ChevronsLeft}
                />
                <Button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                  icon={ChevronLeft}
                />

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }

                  return (
                    <Button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      variant={currentPage === pageNum ? 'primary' : 'outline'}
                      size="sm"
                      className="relative"
                    >
                      {pageNum}
                    </Button>
                  )
                })}

                <Button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                  icon={ChevronRight}
                />
                <Button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                  className="rounded-r-md"
                  icon={ChevronsRight}
                />
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Items per page selector */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">表示件数:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value))
              setCurrentPage(1)
            }}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
          </select>
        </div>
        <div className="text-sm text-gray-700">
          {totalPages}ページ中 {currentPage}
        </div>
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  // Only re-render if users array length changed or user data actually changed
  if (prevProps.users.length !== nextProps.users.length) {
    return false // Re-render if length changed
  }
  
  // Check if any user data actually changed
  for (let i = 0; i < prevProps.users.length; i++) {
    const prevUser = prevProps.users[i]
    const nextUser = nextProps.users[i]
    
    // Check all fields that can be edited or displayed in the table
    if (prevUser.pixUserId !== nextUser.pixUserId ||
        prevUser.userName !== nextUser.userName ||
        prevUser.mailAddress !== nextUser.mailAddress ||
        prevUser.profileImageId !== nextUser.profileImageId ||
        prevUser.filenameExtension !== nextUser.filenameExtension ||
        prevUser.riyoKyotenId !== nextUser.riyoKyotenId ||
        prevUser.riyoKyoten !== nextUser.riyoKyoten ||
        prevUser.deleteFlg !== nextUser.deleteFlg ||
        prevUser.userAuthority !== nextUser.userAuthority ||
        prevUser.riyoKahi !== nextUser.riyoKahi ||
        prevUser.riyoKengen !== nextUser.riyoKengen ||
        prevUser.pixClientUsage !== nextUser.pixClientUsage ||
        prevUser.pixCameraUsage !== nextUser.pixCameraUsage ||
        prevUser.voiceRecognitionUsage !== nextUser.voiceRecognitionUsage ||
        prevUser.ocrUsage !== nextUser.ocrUsage ||
        prevUser.thetaCameraUsage !== nextUser.thetaCameraUsage) {
      return false // Re-render if user data changed
    }
  }
  
  return true // Don't re-render if nothing meaningful changed
})
UserDataTable.displayName = 'UserDataTable'

export default UserDataTable
